import { useBreakpointValue } from '@genial/utils';
import React, { useCallback, useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { useRecoilQuery } from '../../hooks/useRecoilQuery';
import { useRateManagementActions } from '../../store/rateManagement/actions';
import {
  rateManagementCancelledOrderState,
  rateManagementSentOrderState,
  rateManagementUploadedOrderState,
} from '../../store/rateManagement/atom';
import {
  SentOrdersListResponseNormalized,
  sentOrdersQuery,
} from '../../store/rateManagement/selector';

import { ContainerDesktop } from './Desktop';
import { ContainerMobile } from './Mobile';

export const RateManagement = () => {
  const [allData, setAllData] = useState<SentOrdersListResponseNormalized[]>([]);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = React.useState('');
  const { wasCancelled } = useRecoilValue(rateManagementCancelledOrderState);
  const { wasSent } = useRecoilValue(rateManagementSentOrderState);
  const { wasUploaded } = useRecoilValue(rateManagementUploadedOrderState);
  const { resetNotifyOrderCancelled, resetNotifyOrderSent, resetNotifyOrderUploaded } =
    useRateManagementActions();
  const {
    data: sentOrders,
    error: sentOrdersError,
    loading: sentOrdersLoading,
    refresher,
  } = useRecoilQuery(sentOrdersQuery({ page, searchTerm }));

  const handleRefresh = useCallback(() => {
    setAllData([]);
    setPage(1);
    refresher();
  }, [refresher]);

  const handleLoadMore = useCallback(() => {
    if (sentOrders?.meta.pagination.hasNext && !sentOrdersLoading && !sentOrdersError) {
      setPage((prev) => prev + 1);
    }
  }, [sentOrders, sentOrdersLoading, sentOrdersError]);

  const handleClearSearchTerm = React.useCallback(() => {
    setSearchTerm('');
  }, []);

  const responsiveContainer = useBreakpointValue({
    base: (
      <ContainerMobile
        isMobile={true}
        searchTerm={searchTerm}
        onSetSearchTerm={setSearchTerm}
        onClearSearchTerm={handleClearSearchTerm}
        onLoadMore={handleLoadMore}
        sentOrders={allData}
        sentOrdersLoading={sentOrdersLoading}
        sentOrdersError={sentOrdersError}
        onRefresh={handleRefresh}
      />
    ),
    sm: (
      <ContainerDesktop
        isMobile={false}
        searchTerm={searchTerm}
        onSetSearchTerm={setSearchTerm}
        onClearSearchTerm={handleClearSearchTerm}
        onLoadMore={handleLoadMore}
        sentOrders={allData}
        sentOrdersLoading={sentOrdersLoading}
        sentOrdersError={sentOrdersError}
        onRefresh={handleRefresh}
      />
    ),
  });

  useEffect(() => {
    setAllData([]);
    setPage(1);
  }, [searchTerm]);

  useEffect(() => {
    const items = sentOrders?.data;

    if (!sentOrdersLoading && items?.length) {
      setAllData((prev) => [...prev, ...items]);
    }
  }, [sentOrdersLoading, sentOrders, setAllData]);

  useEffect(() => {
    if (wasCancelled || wasSent || wasUploaded) {
      handleRefresh();
    }

    if (wasCancelled) {
      resetNotifyOrderCancelled();
    }

    if (wasSent) {
      resetNotifyOrderSent();
    }

    if (wasUploaded) {
      resetNotifyOrderUploaded();
    }
  }, [
    handleRefresh,
    wasCancelled,
    wasSent,
    wasUploaded,
    resetNotifyOrderCancelled,
    resetNotifyOrderSent,
    resetNotifyOrderUploaded,
  ]);

  return responsiveContainer;
};
